import React, { ReactNode } from "react";

import { Card, CardContent } from "@mui/material";
import Stack from "@mui/material/Stack";
import type { SxProps, Theme } from "@mui/material/styles";

import { IgButton } from "./IgButton";
import { IgIconButton } from "./IgIconButton";
import { IgTypography } from "./IgTypography";
import { mergeSx } from "./utils/mergeSx";

type IgButtonProps = React.ComponentProps<typeof IgButton>;

export interface IgNudgeAlertAction {
  disabled?: boolean;
  label: string;
  onClick: () => void;
  icon?: ReactNode;
  variant?: IgButtonProps["variant"];
  color?: IgButtonProps["color"];
  loading?: boolean;
  sx?: SxProps<Theme>;
}

export interface IgNudgeAlertProps {
  title: string;
  description?: string;
  action?: IgNudgeAlertAction;
  onDismiss?: () => void;
  dismissAriaLabel?: string;
  sx?: SxProps<Theme>;
  /** Extra content rendered below the action, inside the card's content stack. */
  children?: ReactNode;
}

export const IgNudgeAlert = ({
  title,
  description,
  action,
  onDismiss,
  dismissAriaLabel = "Dismiss",
  sx,
  children,
}: IgNudgeAlertProps) => {
  return (
    <Card
      sx={mergeSx(
        {
          maxWidth: 400,
          borderRadius: 2,
          boxShadow: 1,
          position: "relative",
        },
        sx
      )}
    >
      {onDismiss && (
        <IgIconButton
          iconSize={14}
          iconType="close"
          size="small"
          onClick={onDismiss}
          aria-label={dismissAriaLabel}
          sx={{
            position: "absolute",
            right: 4,
            top: 4,
            p: 1,
            zIndex: 1,
          }}
        />
      )}
      {/* MUI pads the last CardContent to 24px at the bottom; the nudge wants
          an even inset on all four sides. */}
      <CardContent sx={{ p: 4, "&:last-child": { pb: 4 } }}>
        <Stack spacing={3}>
          {/* Titles stay on one line and truncate rather than wrap — nudges
              are anchored in the 250px nav drawer, and a wrapped title pushed
              the card taller and orphaned its last word. The dismiss button is
              out of flow, so its footprint is reserved here so the ellipsis
              lands clear of it; description and action keep the full width. */}
          <IgTypography
            variant="h3"
            fontSize={"14px"}
            fontWeight={600}
            noWrap
            title={title}
            sx={onDismiss ? { pr: 2 } : undefined}
          >
            {title}
          </IgTypography>
          {description && (
            <IgTypography variant="body1">{description}</IgTypography>
          )}
          {action && (
            <IgButton
              disabled={action.disabled}
              variant={action.variant ?? "outlined"}
              color={action.color}
              onClick={action.onClick}
              startIcon={action.icon}
              loading={action.loading}
              sx={action.sx}
              fullWidth
            >
              {action.label}
            </IgButton>
          )}
          {children}
        </Stack>
      </CardContent>
    </Card>
  );
};
